import useAuthUser from "../../hooks/useAuthUser";
import useModal from "../../hooks/useModal";
import TableLayout from "../layout/TableLayout";
import {
  NoRecordsFound,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../Table";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import CheckIcon from "@mui/icons-material/Check";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import { TextField, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import useConfirm from "../../hooks/useConfirm";
import { capitalizeFirstLetter } from "../../Helper/functionHelper";
import { approveRejectCancel } from "../../services/wfh";
import { toast } from "react-toastify";
import { useState } from "react";
import ExpandableText from "../common/ExpandableText";
import UserInfoCell from "../common/UserInfoCell";
import Button from "../ui/Button";

const WFH_STATUS_MAP = {
  Pending: { getStatus: () => "Pending", className: "text-warning" },
  Approved: {
    getStatus: (name) => `Approved by ${name}`,
    className: "text-success",
  },
  Rejected: {
    getStatus: (name) => `Rejected by ${name}`,
    className: "text-danger",
  },
  Cancelled: {
    getStatus: (name) => `Cancelled by ${name}`,
    className: "text-danger",
  },
};

const WFHList = ({ wfhData = [], activeFilters, onFilter }) => {
  const tableHeaders = [
    "WFH Type",
    "Date From",
    "Date To",
    "No. of Days",
    "Reason",
    "Approve/Reject Reason",
    "WFH Status",
    "Applied On",
    "Actioned On",
    "Action",
  ];
  const { isAdminHrRole, isManagerRole, isEmployeeRole } = useAuthUser();
  const { showModal, hideModal } = useModal();
  const { confirm } = useConfirm();
  if (isAdminHrRole || isManagerRole) {
    tableHeaders.unshift("Reporting Manager");
    tableHeaders.unshift("Department");
    tableHeaders.unshift("Designation");
    tableHeaders.unshift("Employee Name (Code)");
  }

  const openModal = (title, body) => {
    showModal(title, body);
  };

  const handleWFHActions = async (wfhId, action, remarks = "") => {
    let cancelText = "";
    if (action === "cancel") cancelText = "No";
    const isConfirmed = await confirm({
      title: `${capitalizeFirstLetter(action)} WFH`,
      content: `Are you sure you want to ${action} this WFH?`,
      confirmText: `Yes ${capitalizeFirstLetter(action)}`,
      cancelText: cancelText,
    });

    if (isConfirmed) {
      try {
        const res = await approveRejectCancel(wfhId, action, remarks);
        if (res && res.status === 200) {
          const message = res.data?.message || "WFH updated successfully.";
          toast.success(message);
          onFilter(activeFilters);
        } else {
          const errorMessage =
            res?.data?.message || "Something went wrong. Please try again.";
          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response) {
          const errorMessage = error.response.data?.message || "Server error.";
          toast.error(errorMessage);
        } else {
          toast.error("Network error. Please check your connection.");
        }
      }
    }
  };

  const handleApproveReject = (wfhId, action) => {
    const title = `${capitalizeFirstLetter(action)} WFH`;

    const ApproveRejectForm = () => {
      const [reason, setReason] = useState("");
      const [error, setError] = useState("");
      const [loading, setLoading] = useState(false);

      const handleSubmit = async () => {
        if (action === "reject" && reason.trim() === "") {
          setError("Reason is required for rejection.");
          return;
        }
        setLoading(true);
        try {
          const res = await approveRejectCancel(wfhId, action, reason);
          if (res?.status === 200) {
            toast.success(res.data?.message || "WFH updated successfully.");
            onFilter(activeFilters);
            hideModal();
            setReason("");
            setError("");
          } else {
            toast.error(res?.data?.message || "Something went wrong.");
          }
        } catch (error) {
          toast.error(
            error?.response?.data?.message || "An unexpected error occurred."
          );
        } finally {
          setLoading(false);
        }
      };

      return (
        <div className="py-2">
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            label="Reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError("");
            }}
            error={!!error}
            helperText={error}
          />
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="dark"
              className="me-2"
              onClick={() => hideModal()}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => {
                handleSubmit();
              }}
              disabled={loading}
            >
              {capitalizeFirstLetter(action)}
            </Button>
          </div>
        </div>
      );
    };

    openModal(title, <ApproveRejectForm />);
  };

  return (
    <>
      <TableLayout>
        <Table className="table-col-freeze">
          <TableHeader>
            <TableRow>
              {tableHeaders.map((head, index) => (
                <TableHead
                  key={index}
                  className={
                    index === tableHeaders.length - 1 ? "last-col-freeze" : ""
                  }
                >
                  {head}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {wfhData.length > 0 ? (
              wfhData.map((item, index) => {
                const {
                  status,
                  actionerName,
                  userToken,
                  employeeName,
                  employeeId,
                  reason,
                  designation,
                  department,
                  approveRejectReason,
                  reportingManager,
                  wfhType,
                  startDate,
                  endDate,
                  wfhInDays,
                  createdAt,
                  actionedOn,
                  id,
                } = item;
                const statusData = WFH_STATUS_MAP[status] || {
                  getStatus: () => "--",
                  className: "",
                };
                const statusLabel = statusData.getStatus(actionerName);
                return (
                  <TableRow key={index}>
                    {(isAdminHrRole || isManagerRole) && (
                      <>
                        <TableCell>
                          <UserInfoCell
                            name={employeeName}
                            employeeId={employeeId}
                            token={userToken}
                          />
                        </TableCell>
                        <TableCell>{designation}</TableCell>
                        <TableCell>{department}</TableCell>
                        <TableCell>{reportingManager}</TableCell>
                      </>
                    )}
                    <TableCell>{wfhType}</TableCell>
                    <TableCell>{startDate}</TableCell>
                    <TableCell>{endDate}</TableCell>
                    <TableCell>{wfhInDays}</TableCell>
                    <TableCell>
                      <ExpandableText text={reason} label="WFH Reason" />
                    </TableCell>
                    <TableCell>
                      <ExpandableText
                        text={approveRejectReason}
                        label="Approve/Reject Reason"
                      />
                    </TableCell>
                    <TableCell>
                      <span className={statusData.className}>
                        {statusLabel}
                      </span>
                    </TableCell>
                    <TableCell>{createdAt}</TableCell>
                    <TableCell>{actionedOn || "--"}</TableCell>
                    <TableCell className="last-col-freeze">
                      {isEmployeeRole && status === "Pending" && (
                        <>
                          <Tooltip title="Cancel" placement="top" arrow>
                            <ClearOutlinedIcon
                              className="cursor-pointer"
                              color="error"
                              onClick={() => {
                                handleWFHActions(id, "cancel");
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="Edit" placement="top" arrow>
                            <Link to={`/wfh/edit/${id}`} className="text-dark">
                              <CreateOutlinedIcon className="cursor-pointer" />
                            </Link>
                          </Tooltip>
                        </>
                      )}
                      {(isManagerRole || isAdminHrRole) &&
                        status === "Pending" && (
                          <>
                            <Tooltip title="Approve" placement="top" arrow>
                              <CheckIcon
                                className="cursor-pointer"
                                color="success"
                                onClick={() => {
                                  handleApproveReject(id, "approve");
                                }}
                              />
                            </Tooltip>
                            <Tooltip title="Reject" placement="top" arrow>
                              <DoNotDisturbAltIcon
                                className="cursor-pointer"
                                color="error"
                                onClick={() => {
                                  handleApproveReject(id, "reject");
                                }}
                              />
                            </Tooltip>
                          </>
                        )}
                      {(isManagerRole || isAdminHrRole) &&
                        status === "Approved" &&
                        (() => {
                          const now = new Date();
                          const end = new Date(endDate);
                          const fourDaysAgo = new Date(
                            now.getTime() - 4 * 24 * 60 * 60 * 1000
                          );
                          return end >= fourDaysAgo;
                        })() && (
                          <Tooltip title="Cancel" placement="top" arrow>
                            <ClearOutlinedIcon
                              className="cursor-pointer"
                              color="error"
                              onClick={() => {
                                handleWFHActions(id, "cancel");
                              }}
                            />
                          </Tooltip>
                        )}
                      {isEmployeeRole &&
                        status === "Approved" &&
                        (() => {
                          const start = new Date(startDate);
                          const now = new Date();
                          return start > now;
                        })() && (
                          <Tooltip title="Cancel" placement="top" arrow>
                            <ClearOutlinedIcon
                              className="cursor-pointer"
                              color="error"
                              onClick={() => {
                                handleWFHActions(id, "cancel");
                              }}
                            />
                          </Tooltip>
                        )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <NoRecordsFound colSpan={tableHeaders.length} />
            )}
          </TableBody>
        </Table>
      </TableLayout>
    </>
  );
};

export default WFHList;
